import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useSWR from 'swr';
import {
  Layout,
  LoadingSpinner,
  ErrorMessage,
  MetadataEditor
} from '@/shared/components';
import { useFormSubmission } from '@/shared/hooks/useFormSubmission';
import { pricesApi, doctorsApi } from '@/shared/api';
import { priceItemSchema, type PriceItemFormData } from '@/shared/schemas';

/**
 * Страница редактирования услуги
 * Форма для изменения существующей услуги с валидацией
 *
 * Включает следующие поля:
 * - name: Название услуги (обязательное)
 * - description: Описание услуги (опциональное, до 1000 символов)
 * - categoryId: Категория услуги (обязательное)
 * - doctorId: Привязка к врачу (опциональное)
 * - priceOffline: Цена очной консультации (опциональное)
 * - priceOnline: Цена онлайн консультации (опциональное)
 * - meta: Дополнительные метаданные (опциональное)
 * - isActive: Статус активности
 * - sortOrder: Порядок сортировки
 *
 * @returns JSX элемент страницы редактирования услуги
 */
export const PriceItemEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const itemId = id ? parseInt(id, 10) : 0;

  // Загрузка данных услуги
  const {
    data: itemResponse,
    error: loadError,
    isLoading
  } = useSWR(
    itemId ? ['price-item', itemId] : null,
    () => pricesApi.getItemById(itemId),
    { revalidateOnFocus: false }
  );

  // Загрузка категорий
  const { data: categoriesResponse, error: categoriesError } = useSWR(
    'price-categories-for-select',
    () => pricesApi.getAllCategories({ includeInactive: false }),
    { revalidateOnFocus: false }
  );

  // Загрузка врачей
  const { data: doctorsResponse, error: doctorsError } = useSWR(
    'doctors-for-select',
    () => doctorsApi.getAll({ includeInactive: false }),
    { revalidateOnFocus: false }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control
  } = useForm<PriceItemFormData>({
    resolver: zodResolver(priceItemSchema),
    defaultValues: {
      categoryId: 0,
      doctorId: null,
      name: '',
      description: null,
      priceOffline: null,
      priceOnline: null,
      meta: null,
      isActive: true,
      sortOrder: 0
    }
  });

  const categories = categoriesResponse?.categories || [];
  const doctors = doctorsResponse?.doctors || [];

  /**
   * Хук для управления отправкой формы с уведомлениями
   */
  const { isSubmitting, handleSubmit: handleFormSubmit } =
    useFormSubmission<PriceItemFormData>({
      onSubmit: async (data: PriceItemFormData) => {
        // Преобразуем данные для API, правильно обрабатывая опциональные свойства
        const apiData = {
          categoryId: data.categoryId,
          name: data.name,
          isActive: data.isActive,
          sortOrder: data.sortOrder,
          // Convert undefined to null for API compatibility
          doctorId: data.doctorId ?? null,
          // Convert undefined to null for API compatibility
          meta: data.meta ?? null,
          ...(data.description !== undefined &&
            data.description !== null && { description: data.description }),
          ...(data.priceOffline !== undefined && {
            priceOffline: data.priceOffline
          }),
          ...(data.priceOnline !== undefined && {
            priceOnline: data.priceOnline
          })
        };

        const response = await pricesApi.updateItem(itemId, apiData);

        if (response.success) {
          // Перенаправляем на страницу цен через 2 секунды
          setTimeout(() => {
            navigate('/prices');
          }, 2000);
        }
      },
      successMessage: 'Услуга успешно обновлена',
      errorMessage: 'Ошибка при обновлении услуги'
    });

  /**
   * Заполнение формы данными услуги при загрузке
   */
  useEffect(() => {
    if (itemResponse?.success && itemResponse.item) {
      const item = itemResponse.item;
      reset({
        categoryId: item.categoryId,
        doctorId: item.doctorId || null, // Ensure null instead of undefined
        name: item.name,
        description: item.description || '',
        priceOffline: item.priceOffline || '',
        priceOnline: item.priceOnline || '',
        meta: item.meta
          ? (() => {
              try {
                return JSON.parse(item.meta);
              } catch {
                return null;
              }
            })()
          : null,
        isActive: item.isActive,
        sortOrder: item.sortOrder
      });
    }
  }, [itemResponse, reset]);

  /**
   * Обработчик отмены
   */
  const handleCancel = (): void => {
    navigate('/prices');
  };

  // Проверка валидности ID
  if (!itemId || isNaN(itemId)) {
    return (
      <Layout title="Ошибка">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <ErrorMessage message="Неверный ID услуги" />
          </div>
        </div>
      </Layout>
    );
  }

  // Состояние загрузки
  if (isLoading) {
    return (
      <Layout title="Загрузка...">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" text="Загрузка данных услуги..." />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Ошибка загрузки
  if (loadError || !itemResponse?.success || categoriesError || doctorsError) {
    return (
      <Layout title="Ошибка">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <ErrorMessage message="Ошибка загрузки данных. Попробуйте обновить страницу." />
          </div>
        </div>
      </Layout>
    );
  }

  const item = itemResponse.item;

  return (
    <Layout title={`Редактировать: ${item.name}`}>
      <div className="space-y-6">
        {/* Заголовок */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-base-content">
              Редактировать услугу
            </h2>
            <p className="text-base-content/60 mt-1">
              Изменение услуги "{item.name}"
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Назад к списку
          </button>
        </div>

        {/* Форма */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="space-y-6"
            >
              {/* Название */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Название услуги <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className={`input input-bordered w-full ${errors.name ? 'input-error' : ''}`}
                  placeholder="Например: Первичная консультация педиатра"
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.name.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Описание */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Описание услуги
                  </span>
                </label>
                <textarea
                  {...register('description')}
                  className={`textarea textarea-bordered w-full ${errors.description ? 'textarea-error' : ''}`}
                  placeholder="Подробное описание услуги, особенности проведения, что включает в себя консультация..."
                  rows={4}
                  disabled={isSubmitting}
                />
                {errors.description && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.description.message}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Опциональное поле. Максимум 1000 символов
                  </span>
                </label>
              </div>

              {/* Категория */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Категория <span className="text-error">*</span>
                  </span>
                </label>
                <select
                  {...register('categoryId', { valueAsNumber: true })}
                  className={`select select-bordered w-full ${errors.categoryId ? 'select-error' : ''}`}
                  disabled={isSubmitting}
                >
                  <option value={0}>Выберите категорию</option>
                  {categories.map((category: { id: number; name: string }) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.categoryId && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.categoryId.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Врач */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Врач</span>
                </label>
                <select
                  {...register('doctorId', {
                    setValueAs: (value) => {
                      // Handle empty string, '0', or null/undefined values
                      if (
                        value === '' ||
                        value === '0' ||
                        value === null ||
                        value === undefined
                      ) {
                        return null;
                      }
                      // Parse the value as integer
                      const parsed = parseInt(value, 10);
                      // Return null if parsing results in NaN
                      return isNaN(parsed) ? null : parsed;
                    }
                  })}
                  className={`select select-bordered w-full ${errors.doctorId ? 'select-error' : ''}`}
                  disabled={isSubmitting}
                >
                  <option value="">Не указан</option>
                  {doctors.map(
                    (doctor: {
                      id: number;
                      fullName: string;
                      specialization: string;
                    }) => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.fullName} - {doctor.specialization}
                      </option>
                    )
                  )}
                </select>
                {errors.doctorId && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.doctorId.message}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Оставьте пустым, если услуга не привязана к конкретному
                    врачу
                  </span>
                </label>
              </div>

              {/* Цены */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Цена очной консультации */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">
                      Цена очной консультации (₽)
                    </span>
                  </label>
                  <input
                    type="string"
                    {...register('priceOffline')}
                    className={`input input-bordered w-full ${errors.priceOffline ? 'input-error' : ''}`}
                    placeholder="2000"
                    min="0"
                    disabled={isSubmitting}
                  />
                  {errors.priceOffline && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.priceOffline.message}
                      </span>
                    </label>
                  )}
                </div>

                {/* Цена онлайн консультации */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">
                      Цена онлайн консультации (₽)
                    </span>
                  </label>
                  <input
                    type="string"
                    {...register('priceOnline')}
                    className={`input input-bordered w-full ${errors.priceOnline ? 'input-error' : ''}`}
                    placeholder="1500"
                    min="0"
                    disabled={isSubmitting}
                  />
                  {errors.priceOnline && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.priceOnline.message}
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {/* Метаданные */}
              <MetadataEditor
                control={control}
                name="meta"
                title="Дополнительные атрибуты"
                description="Пользовательские метаданные для услуги в формате ключ-значение"
                disabled={isSubmitting}
              />

              {/* Порядок сортировки */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Порядок сортировки
                  </span>
                </label>
                <input
                  type="number"
                  {...register('sortOrder', { valueAsNumber: true })}
                  className={`input input-bordered w-full ${errors.sortOrder ? 'input-error' : ''}`}
                  placeholder="0"
                  min="0"
                  disabled={true}
                  readOnly
                />
                {errors.sortOrder && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.sortOrder.message}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Порядок сортировки управляется перетаскиванием на странице
                    категории услуг
                  </span>
                </label>
              </div>

              {/* Статус активности */}
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="checkbox checkbox-primary"
                    disabled={isSubmitting}
                  />
                  <span className="label-text font-medium">
                    Активная услуга
                  </span>
                </label>
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Неактивные услуги не отображаются на сайте
                  </span>
                </label>
              </div>

              {/* Информация об услуге */}
              <div className="bg-base-200 p-4 rounded-lg">
                <h4 className="font-medium text-base-content mb-2">
                  Информация об услуге
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-base-content/60">Категория:</span>
                    <span className="ml-2 font-medium">
                      {item.category?.name || 'Не указана'}
                    </span>
                  </div>
                  <div>
                    <span className="text-base-content/60">Создана:</span>
                    <span className="ml-2 font-medium">
                      {new Date(item.createdAt).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                  {item.doctor && (
                    <div className="sm:col-span-2">
                      <span className="text-base-content/60">
                        Текущий врач:
                      </span>
                      <span className="ml-2 font-medium">
                        {item.doctor.fullName} - {item.doctor.specialization}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Кнопки действий */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  type="submit"
                  className="btn btn-primary flex-1 sm:flex-none"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Сохранение...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Сохранить изменения
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-secondary flex-1 sm:flex-none"
                  disabled={isSubmitting}
                >
                  <i className="fas fa-times"></i>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};
